import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  GoabGrid,
  GoabContainer,
  GoabFormItem,
  GoabInputText,
  GoabDropdown,
  GoabDropdownItem,
  GoabDatePicker,
  GoabRadioGroup,
  GoabRadioItem,
  GoabSpacer,
  GoabDivider,
  GoabButtonGroup,
  GoabButton,
  GoabModal,
  GoabCallout,
  GoabText,
} from "@abgov/react-components";
import DisplayContent from "../../components/DynamicForm/Forms/DisplayContent";
import FirstNationStatusDetail from "../FNIM/FirstNationStatusDetail";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useStackDatePickerFieldsOnMobile } from "../../hooks/useStackDatePickerFieldsOnMobile";
import {
  domainKey,
  refCategories,
  toTitleCase,
  NoAvailDocumentTypeId,
  nameRegExp,
  bandRegExp,
  familyRegExp,
  positionRegExp,
  fnibRegExp,
  calculateAge,
  normalizeName,
  formatDateForDatabase,
} from "../../utils/globalUtility";

// Due to dynamic nature of family member, there is some code duplication for validation.
// Could benefit from a refactor to utilize parent form errors.
const getYesterday = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
};

const firstNationsFieldNames = [
  "isIndigenous",
  "band",
  "family",
  "position",
  "FNIHB",
  "livesOnReserve",
];

const getFamilyMemberSchema = (disabledFeatures = []) => {
  const isFirstNationsDisabled = disabledFeatures.includes("firstNations");

  return Yup.object().shape({
    relationship: Yup.string().required(
      "RESOURCES@familyMemberForm.relationship.error",
    ),
    givenName: Yup.string()
      .matches(nameRegExp, "RESOURCES@form.name.invalidError")
      .max(50, "RESOURCES@form.givenName.maxError")
      .trim()
      .required("RESOURCES@form.givenName.error"),
    middleName: Yup.string()
      .matches(nameRegExp, "RESOURCES@form.name.invalidError")
      .max(50, "RESOURCES@form.middleName.maxError")
      .trim(),
    surname: Yup.string()
      .matches(nameRegExp, "RESOURCES@form.name.invalidError")
      .max(50, "RESOURCES@form.surname.maxError")
      .trim()
      .required("RESOURCES@form.surname.error"),
    dob: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === "" ||
          originalValue === null ||
          originalValue === undefined
        ) {
          return null;
        }
        return value;
      })
      .max(getYesterday(), "RESOURCES@form.dob.invalidError")
      .required("RESOURCES@form.dob.error")
      .when("relationship", (relationship, schema) => {
        return schema.test("age-relationship", function (value) {
          if (!relationship || !value) return true;

          const age = calculateAge(value);

          if (relationship.includes("child")) {
            if (age >= 18) {
              return this.createError({
                message: "form.dob.child.invalidError",
              });
            }
          }

          if (relationship.includes("adult")) {
            if (age < 18) {
              return this.createError({
                message: "form.dob.adult.invalidError",
              });
            }
          }

          if (relationship.includes("spouse")) {
            if (age < 18) {
              return this.createError({
                message: "form.dob.spouse.invalidError",
              });
            }
          }

          if (relationship.includes("elderly")) {
            if (age < 18) {
              return this.createError({
                message: "form.dob.elderly.invalidError",
              });
            }
          }

          return true;
        });
      }),
    gender: Yup.string().required("RESOURCES@form.gender.invalidError"),
    isIndigenous: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().required("RESOURCES@global.error.selectAnswer"),
    band: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().when("isIndigenous", {
          is: (isIndigenous) => isIndigenous === "Y",
          then: () =>
            Yup.string().matches(
              bandRegExp,
              "RESOURCES@global.error.invalidLength",
            ),
        }),
    family: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().when("isIndigenous", {
          is: (isIndigenous) => isIndigenous === "Y",
          then: () =>
            Yup.string().matches(
              familyRegExp,
              "RESOURCES@global.error.invalidLength",
            ),
        }),
    position: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().when("isIndigenous", {
          is: (isIndigenous) => isIndigenous === "Y",
          then: () =>
            Yup.string().matches(
              positionRegExp,
              "RESOURCES@global.error.invalidLength",
            ),
        }),
    FNIHB: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().when("isIndigenous", {
          is: (isIndigenous) => isIndigenous === "Y",
          then: () =>
            Yup.string().matches(
              fnibRegExp,
              "RESOURCES@fnihb.error.invalidLength",
            ),
        }),
    livesOnReserve: isFirstNationsDisabled
      ? Yup.string()
      : Yup.string().when("isIndigenous", {
          is: (isIndigenous) => isIndigenous === "Y",
          then: () =>
            Yup.string().required("RESOURCES@global.error.selectAnswer"),
        }),
  });
};

// Used to conditionally filter out indigenous properties if isIndigenous is selected as N.
const getVisibleFields = (formValues, disabledFeatures = []) => {
  const schema = getFamilyMemberSchema(disabledFeatures);
  const allFields = Object.keys(schema.fields);
  let filteredFields = [];

  if (formValues.isIndigenous === "N") {
    filteredFields = allFields.filter((fieldName) => {
      const field = schema.fields[fieldName];
      return !field.deps.includes("isIndigenous");
    });
  } else {
    filteredFields = allFields;
  }

  if (disabledFeatures.includes("firstNations")) {
    filteredFields = filteredFields.filter(
      (fieldName) => !firstNationsFieldNames.includes(fieldName),
    );
  }

  return filteredFields;
};

const filterFamilyMemberData = (familyMemberData, disabledFeatures = []) => {
  const visibleFields = getVisibleFields(familyMemberData, disabledFeatures);

  return Object.fromEntries(
    Object.entries(familyMemberData).filter(
      ([key]) => visibleFields.includes(key) || key === "id",
    ),
  );
};

// Need a custom scroll method due to family members widget being dynamic
// and using its own formik instance unlike other widgets.
const scrollToFirstError = (errors) => {
  if (Object.keys(errors).length > 0) {
    setTimeout(() => {
      const fieldId = Object.keys(errors)[0];

      requestAnimationFrame(() => {
        const errorElement =
          document.querySelector(`[name="${fieldId}"]`) ||
          document.querySelector(`[id="${fieldId}"]`) ||
          document.querySelector(`[aria-labelledby="${fieldId}"]`);

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      });
    }, 100);
  }
};

const FamilyMember = ({
  familyMember,
  setShowDeleteFamilyMember,
  addUpdateFamilyMember,
  getResourceValue,
  getRefData,
  existingFamilyMembers = [],
  supportInfo = "",
  disabledFeatures,
  relationshipOptions,
}) => {
  const relationshipRefData = getRefData({
    category: refCategories.RELATIONSHIP,
    domainKey,
    filterOptions: relationshipOptions,
  });
  const gendersRefData = getRefData({
    category: refCategories.GENDER,
    domainKey,
  });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  const isMobile = useIsMobile();
  // Stacks the date picker's internal month/day/year fields on mobile instead
  // of letting them overflow the viewport (see hook for details).
  const dobDatePickerRef = useStackDatePickerFieldsOnMobile(isMobile);

  const validationSchema = useMemo(
    () => getFamilyMemberSchema(disabledFeatures),
    [disabledFeatures],
  );

  // Helper function to check for duplicate family members
  const checkForDuplicate = (values) => {
    if (!values.givenName || !values.surname || !values.dob) {
      return false; // Can't check for duplicates if required fields are missing
    }

    const currentId = familyMember?.id;
    const normalizedGivenName = normalizeName(values.givenName);
    const normalizedSurname = normalizeName(values.surname);
    const dobString = formatDateForDatabase(values.dob);

    return existingFamilyMembers.some((member) => {
      // Skip the current member when editing (check for undefined to handle falsy IDs like empty string)
      if (currentId !== undefined && member.id === currentId) {
        return false;
      }

      const memberGivenName = normalizeName(member.givenName);
      const memberSurname = normalizeName(member.surname);
      const memberDobString = formatDateForDatabase(member.dob);

      return (
        memberGivenName === normalizedGivenName &&
        memberSurname === normalizedSurname &&
        memberDobString === dobString
      );
    });
  };

  const familyMemberForm = useFormik({
    initialValues: {
      relationship: familyMember?.relationship ?? "",
      givenName: familyMember?.givenName ?? "",
      middleName: familyMember?.middleName ?? "",
      surname: familyMember?.surname ?? "",
      dob: familyMember?.dob ?? null,
      gender: familyMember?.gender ?? "",
      isIndigenous: familyMember?.isIndigenous ?? "",
      band: familyMember?.band,
      family: familyMember?.family,
      position: familyMember?.position,
      FNIHB: familyMember?.FNIHB,
      livesOnReserve: familyMember?.livesOnReserve,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false, // Disable automatic validation on blur
    onSubmit: async (values) => {
      // Check for duplicates before submitting
      if (checkForDuplicate(values)) {
        setDuplicateError(true);
        // Scroll to top to show the callout
        window.scrollTo({ top: 0, behavior: "smooth" });
        return; // Prevent submission
      }

      if (values.documentType === NoAvailDocumentTypeId) {
        values.documentNumber = "";
      }
      const filteredFamilyMemberValues = filterFamilyMemberData(
        values,
        disabledFeatures,
      );
      addUpdateFamilyMember({
        ...filteredFamilyMemberValues,
        id: familyMember?.id,
      });
    },
  });

  // Allow scroll to error on form submit
  const handleSubmit = () => {
    setHasSubmitted(true);
    familyMemberForm.handleSubmit();
  };

  useEffect(() => {
    if (hasSubmitted && Object.keys(familyMemberForm.errors).length > 0) {
      scrollToFirstError(familyMemberForm.errors);
    }
    // Reset hasSubmitted after checking for errors
    if (hasSubmitted) {
      setHasSubmitted(false);
    }
  }, [hasSubmitted, familyMemberForm.errors]);

  // Clear duplicate error when any critical field value changes.
  useEffect(() => {
    if (!duplicateError) {
      return;
    }
    setDuplicateError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    familyMemberForm.values.givenName,
    familyMemberForm.values.surname,
    familyMemberForm.values.dob,
  ]);

  // Relationship-dependent age validation must be re-checked when relationship changes,
  // since dob may not be touched/blurred again.
  useEffect(() => {
    if (familyMemberForm.values.dob) {
      familyMemberForm.validateField("dob");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyMemberForm.values.relationship]);

  // The dob field mixes schema messages ("RESOURCES@...") with
  // relationship-age messages, and overrides the default required message.
  const getDobErrorMessage = () => {
    if (!familyMemberForm.errors.dob || !familyMemberForm.touched.dob) {
      return undefined;
    }

    if (familyMemberForm.errors.dob === "RESOURCES@form.dob.error") {
      return getResourceValue({ key: "RESOURCES@familyMemberForm.dob.error" });
    }

    const errorKey = familyMemberForm.errors.dob.startsWith("RESOURCES@")
      ? familyMemberForm.errors.dob
      : `RESOURCES@${familyMemberForm.errors.dob}`;

    return getResourceValue({ key: errorKey });
  };

  const handleChange = (name, value, titleCase = false) => {
    delete familyMemberForm.errors[name];
    familyMemberForm.setFieldValue(
      name,
      titleCase ? toTitleCase(value) : value,
    );
  };

  const handleBlur = (name) => {
    familyMemberForm.setFieldTouched(name, true);
    // Only validate per field blur
    familyMemberForm.validateField(name);
  };

  const onConfirmCancel = () => {
    if (!familyMemberForm.dirty) {
      addUpdateFamilyMember();
    }
    setConfirmCancel(true);
  };

  const onCancel = () => {
    setConfirmCancel(false);
    addUpdateFamilyMember();
  };

  return (
    <>
      <GoabContainer maxWidth="100%" accent="thin" type="interactive">
        <h3>
          {Object.keys(familyMember ?? {})?.length > 0 &&
          !familyMember?.isGenerated
            ? getResourceValue({
                key: "RESOURCES@familyMemberForm.editFamilyMember",
              })
            : getResourceValue({
                key: "RESOURCES@familyMemberForm.addFamilyMember",
              })}
        </h3>

        {duplicateError && (
          <>
            <GoabSpacer vSpacing="m" />
            <GoabCallout type="emergency" emphasis="low">
              {getResourceValue({
                key: "RESOURCES@familyMemberForm.duplicate.error",
              })}
              <GoabSpacer vSpacing="s" />
              <DisplayContent content={supportInfo} />
            </GoabCallout>
          </>
        )}
        <GoabSpacer vSpacing="xl" />

        <div style={{ containerType: "inline-size" }}>
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@familyMemberForm.relationship.label",
            })}
            error={
              familyMemberForm.errors.relationship &&
              familyMemberForm.touched.relationship &&
              getResourceValue({ key: familyMemberForm.errors.relationship })
            }
            id="relationship"
          >
            <GoabDropdown
              name="relationship"
              placeholder={getResourceValue({
                key: "RESOURCES@global.select.placeholder",
              })}
              maxWidth="100%"
              width="398px"
              value={familyMemberForm.values.relationship}
              onChange={({ name, value, titleCase }) =>
                handleChange(name, value, titleCase)
              }
              onBlur={({ name }) => handleBlur(name)}
              arialabelledby="relationship"
              error={
                familyMemberForm.errors.relationship &&
                familyMemberForm.touched.relationship &&
                getResourceValue({ key: familyMemberForm.errors.relationship })
              }
            >
              {(relationshipRefData ?? []).map(
                ({ description: label, code: value }) => {
                  const allowsMultiples =
                    relationshipOptions?.find(
                      (option) => option.code === value,
                    )?.allowMultiples !== false;
                  const alreadyUsed = existingFamilyMembers.some(
                    (member) =>
                      member.relationship === value &&
                      member.id !== familyMember?.id,
                  );

                  const disabled = !allowsMultiples && alreadyUsed;

                  if (!disabled) {
                    return (
                      <GoabDropdownItem
                        key={value}
                        value={value}
                        label={label}
                      />
                    );
                  }
                },
              )}
            </GoabDropdown>
          </GoabFormItem>
        </div>

        <GoabGrid minChildWidth="30ch" mt="l">
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.givenName.label",
            })}
            error={
              familyMemberForm.errors.givenName &&
              familyMemberForm.touched.givenName &&
              getResourceValue({ key: familyMemberForm.errors.givenName })
            }
            id="givenName"
          >
            <GoabInputText
              name="givenName"
              value={familyMemberForm.values.givenName}
              onChange={({ name, value, titleCase }) =>
                handleChange(name, value, titleCase)
              }
              onBlur={({ name }) => handleBlur(name)}
              maxLength={50}
              error={
                familyMemberForm.errors.givenName &&
                familyMemberForm.touched.givenName &&
                getResourceValue({ key: familyMemberForm.errors.givenName })
              }
            />
          </GoabFormItem>
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.middleName.label",
            })}
            error={
              familyMemberForm.errors.middleName &&
              familyMemberForm.touched.middleName &&
              getResourceValue({ key: familyMemberForm.errors.middleName })
            }
            requirement="optional"
            id="middleName"
          >
            <GoabInputText
              name="middleName"
              value={familyMemberForm.values.middleName}
              onChange={({ name, value, titleCase }) =>
                handleChange(name, value, titleCase)
              }
              onBlur={({ name }) => handleBlur(name)}
              maxLength={50}
              error={
                familyMemberForm.errors.middleName &&
                familyMemberForm.touched.middleName &&
                getResourceValue({ key: familyMemberForm.errors.middleName })
              }
            />
          </GoabFormItem>
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.surname.label",
            })}
            error={
              familyMemberForm.errors.surname &&
              familyMemberForm.touched.surname &&
              getResourceValue({ key: familyMemberForm.errors.surname })
            }
            id="surname"
          >
            <GoabInputText
              name="surname"
              value={familyMemberForm.values.surname}
              onChange={({ name, value, titleCase }) =>
                handleChange(name, value, titleCase)
              }
              onBlur={({ name }) => handleBlur(name)}
              maxLength={50}
              error={
                familyMemberForm.errors.surname &&
                familyMemberForm.touched.surname &&
                getResourceValue({ key: familyMemberForm.errors.surname })
              }
            />
          </GoabFormItem>
        </GoabGrid>

        <GoabSpacer vSpacing="l" />

        <GoabFormItem
          label={getResourceValue({ key: "RESOURCES@form.dob.label" })}
          error={getDobErrorMessage()}
          id="dob"
        >
          {/* GoabDatePicker's type="input" variant stacks month/day/year via
              useStackDatePickerFieldsOnMobile below instead of overflowing on
              mobile (see hook for details). Note: type="calendar" was tried
              instead, but its internal month/year navigation controls leak a
              blur event with name="month"/"year" up to our onBlur handler,
              which breaks Yup validation since the schema has no such path -
              so we stay on type="input" and fix the overflow via the hook. */}
          <div ref={dobDatePickerRef}>
            <GoabDatePicker
              name="dob"
              type="input"
              value={familyMemberForm.values.dob || undefined}
              min="1900-01-01"
              max={getYesterday()}
              error={!!getDobErrorMessage()}
              onChange={({ name, valueStr }) => {
                if (valueStr) {
                  handleChange(name, valueStr);
                }
              }}
              onBlur={({ name }) => handleBlur(name)}
            />
          </div>
        </GoabFormItem>
        <GoabSpacer vSpacing="l" />
        <GoabFormItem
          label={getResourceValue({
            key: "RESOURCES@form.gender.label",
          })}
          error={
            familyMemberForm.errors.gender &&
            familyMemberForm.touched.gender &&
            getResourceValue({ key: familyMemberForm.errors.gender })
          }
          id="gender"
        >
          <GoabDropdown
            name="gender"
            placeholder={getResourceValue({
              key: "RESOURCES@global.select.placeholder",
            })}
            width="60%"
            value={familyMemberForm.values.gender}
            onChange={({ name, value, titleCase }) =>
              handleChange(name, value, titleCase)
            }
            onBlur={({ name }) => handleBlur(name)}
            arialabelledby="gender"
            error={
              familyMemberForm.errors.gender &&
              familyMemberForm.touched.gender &&
              getResourceValue({ key: familyMemberForm.errors.gender })
            }
          >
            {(gendersRefData ?? []).map(
              ({ description: label, code: value }) => {
                return (
                  <GoabDropdownItem key={value} value={value} label={label} />
                );
              },
            )}
          </GoabDropdown>
        </GoabFormItem>
        <GoabText size="heading-2xs" mt="l" mb="none">
          * Proof of identity and residence for family members may be requested.
        </GoabText>

        {!disabledFeatures.includes("firstNations") && (
          <>
            <GoabDivider mt="l" mb="l"></GoabDivider>

            <GoabFormItem
              label={getResourceValue({
                key: "RESOURCES@familyMemberForm.isIndigenous.question",
              })}
              error={
                familyMemberForm.errors.isIndigenous &&
                familyMemberForm.touched.isIndigenous &&
                getResourceValue({ key: familyMemberForm.errors.isIndigenous })
              }
              id="isIndigenous"
            >
              <GoabRadioGroup
                orientation="horizontal"
                name="isIndigenous"
                value={familyMemberForm.values.isIndigenous}
                onChange={({ name, value, titleCase }) =>
                  handleChange(name, value, titleCase)
                }
                onBlur={({ name }) => handleBlur(name)}
                error={
                  familyMemberForm.errors.isIndigenous &&
                  familyMemberForm.touched.isIndigenous &&
                  getResourceValue({
                    key: familyMemberForm.errors.isIndigenous,
                  })
                }
              >
                <GoabRadioItem label="Yes" value="Y" />
                <GoabRadioItem label="No" value="N" />
              </GoabRadioGroup>
            </GoabFormItem>

            {familyMemberForm.values.isIndigenous === "Y" && (
              <div>
                <GoabDivider mt="xl" mb="xl"></GoabDivider>
                <div>
                  <FirstNationStatusDetail
                    form={familyMemberForm}
                    getResourceValue={getResourceValue}
                    pageResourceKey="familyMember"
                    showBorder={false}
                  />
                </div>

                <GoabDivider mt="xl" mb="xl"></GoabDivider>

                <GoabFormItem
                  label={getResourceValue({
                    key: "RESOURCES@familyMember.livesOnReserve.question",
                  })}
                  error={
                    familyMemberForm.errors.livesOnReserve &&
                    familyMemberForm.touched.livesOnReserve &&
                    getResourceValue({
                      key: familyMemberForm.errors.livesOnReserve,
                    })
                  }
                  id="livesOnReserve"
                >
                  <GoabRadioGroup
                    orientation="horizontal"
                    name="livesOnReserve"
                    value={familyMemberForm.values.livesOnReserve}
                    onChange={({ name, value, titleCase }) =>
                      handleChange(name, value, titleCase)
                    }
                    onBlur={({ name }) => handleBlur(name)}
                    error={
                      familyMemberForm.errors.livesOnReserve &&
                      familyMemberForm.touched.livesOnReserve &&
                      getResourceValue({
                        key: familyMemberForm.errors.livesOnReserve,
                      })
                    }
                  >
                    <GoabRadioItem label="Yes" value="Y" />
                    <GoabRadioItem label="No" value="N" />
                  </GoabRadioGroup>
                </GoabFormItem>
              </div>
            )}
          </>
        )}

        <GoabSpacer vSpacing="xl" />

        <GoabButtonGroup>
          {Object.keys(familyMember ?? {})?.length > 0 &&
            !familyMember?.isGenerated && (
              <GoabButton
                type="primary"
                onClick={handleSubmit}
                testId="save-btn"
              >
                {getResourceValue({ key: "RESOURCES@global.button.save" })}
              </GoabButton>
            )}

          {(!Object.keys(familyMember ?? {})?.length ||
            familyMember?.isGenerated) && (
            <GoabButton
              type="primary"
              leadingIcon="add"
              onClick={handleSubmit}
              testId="add-btn"
            >
              {getResourceValue({ key: "RESOURCES@global.button.add" })}
            </GoabButton>
          )}

          <GoabButton type="secondary" onClick={onConfirmCancel}>
            {getResourceValue({ key: "RESOURCES@global.button.cancel" })}
          </GoabButton>

          {Object.keys(familyMember ?? {})?.length > 0 &&
            !familyMember?.isGenerated && (
              <GoabButton
                type="tertiary"
                variant="destructive"
                onClick={() => {
                  setShowDeleteFamilyMember(familyMember);
                }}
              >
                {getResourceValue({ key: "RESOURCES@global.button.delete" })}
              </GoabButton>
            )}
        </GoabButtonGroup>
        <GoabModal
          heading={getResourceValue({ key: "RESOURCES@cancel.modal.header" })}
          role="alertdialog"
          open={confirmCancel}
        >
          {getResourceValue({ key: "RESOURCES@cancel.modal.content" })}
          <GoabButtonGroup alignment="end" mt="xl">
            <GoabButton
              type="secondary"
              onClick={() => setConfirmCancel(false)}
            >
              {getResourceValue({ key: "RESOURCES@global.button.cancel" })}
            </GoabButton>
            <GoabButton type="primary" onClick={onCancel}>
              {getResourceValue({ key: "RESOURCES@global.button.confirm" })}
            </GoabButton>
          </GoabButtonGroup>
        </GoabModal>
      </GoabContainer>
    </>
  );
};

export default FamilyMember;
